import {
    DEFAULT_ROOM_CONFIG,
    encodeChildrenAges,
    MAX_ADULTS_PER_ROOM,
    MAX_CHILDREN_PER_ROOM,
    MAX_ROOMS,
    MIN_ADULTS_PER_ROOM,
    MIN_CHILDREN_PER_ROOM,
    MIN_ROOMS,
    resizeChildAges,
    type HotelRoomConfig
} from '@/src/components/common/home/hotelSearch/searchForm/hooks/useHotelSearchManager'
import { useCallback, useMemo, useState } from 'react'

/** Matches the stay the page pre-fills: one room, two adults. */
const DEFAULT_ROOMS: HotelRoomConfig[] = [{ ...DEFAULT_ROOM_CONFIG, adults: 2 }]

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

/**
 * Rooms and guests for the property (SEO) page, which arrives from a search engine with no
 * occupancy in the URL.
 *
 * Draft is what the selector edits, applied is what the search asks for. They are separate
 * because the search re-runs whenever its params change, and every +/- tap would otherwise
 * be a search of its own.
 */
const usePropertyOccupancy = () => {
    const [rooms, setRooms] = useState<HotelRoomConfig[]>(DEFAULT_ROOMS)
    const [appliedRooms, setAppliedRooms] = useState<HotelRoomConfig[]>(DEFAULT_ROOMS)

    /** Commit the draft — called when the selector closes. */
    const applyRooms = useCallback(() => setAppliedRooms(rooms), [rooms])

    const setRoomCount = useCallback((count: number) => {
        setRooms(prev => {
            const next = clamp(count, MIN_ROOMS, MAX_ROOMS)
            if (next === prev.length) return prev
            if (next < prev.length) return prev.slice(0, next)
            const added = Array.from({ length: next - prev.length }, () => ({ ...DEFAULT_ROOM_CONFIG }))
            return [...prev, ...added]
        })
    }, [])

    const setRoomAdults = useCallback((roomIndex: number, adults: number) => {
        const next = clamp(adults, MIN_ADULTS_PER_ROOM, MAX_ADULTS_PER_ROOM)
        setRooms(prev => prev.map((room, index) => (index === roomIndex ? { ...room, adults: next } : room)))
    }, [])

    const setRoomChildren = useCallback((roomIndex: number, children: number) => {
        const next = clamp(children, MIN_CHILDREN_PER_ROOM, MAX_CHILDREN_PER_ROOM)
        setRooms(prev =>
            prev.map((room, index) =>
                index === roomIndex
                    ? { ...room, children: next, childAges: resizeChildAges(room.childAges, next) }
                    : room
            )
        )
    }, [])

    // The shape searchHotels takes: counts per room, comma-joined.
    const searchParams = useMemo(
        () => ({
            noOfRooms: String(appliedRooms.length),
            noOfAdults: appliedRooms.map(room => room.adults).join(','),
            noOfChildren: appliedRooms.map(room => room.children).join(','),
            childrenAges: encodeChildrenAges(appliedRooms)
        }),
        [appliedRooms]
    )

    return {
        rooms,
        searchParams,
        applyRooms,
        setRoomCount,
        setRoomAdults,
        setRoomChildren,
        limits: {
            minRooms: MIN_ROOMS,
            maxRooms: MAX_ROOMS,
            minAdultsPerRoom: MIN_ADULTS_PER_ROOM,
            maxAdultsPerRoom: MAX_ADULTS_PER_ROOM,
            minChildrenPerRoom: MIN_CHILDREN_PER_ROOM,
            maxChildrenPerRoom: MAX_CHILDREN_PER_ROOM
        }
    }
}

export type PropertyOccupancy = ReturnType<typeof usePropertyOccupancy>

export default usePropertyOccupancy
